/**
 * Explicit (de)serialization between the camelCase TypeScript models and the
 * PascalCase GlobalConfiguration document Data payloads.
 *
 * PascalCase on the wire is the container convention (every pre-existing
 * document family is PascalCase). Field names inside ConstantFields are the
 * PROVIDER's wire fields (e.g. "idType") and are passed through verbatim in
 * both directions — a generic case transform would corrupt them.
 *
 * Reads tolerate both casings so early camelCase dev documents keep loading.
 */

import type {
  CategoryFieldDefaults,
  CompanyMapping,
  OrganizationFieldDefault,
  ProviderConfigurationData,
  ValueMappingRow,
  ValueMappingSetData,
} from "@/types/payroll"

type Raw = Record<string, unknown> | null | undefined

function pick<T>(raw: Raw, pascal: string, fallback: T): T {
  if (!raw || typeof raw !== "object") return fallback
  const camel = pascal.charAt(0).toLowerCase() + pascal.slice(1)
  const value = (raw as Record<string, unknown>)[pascal] ?? (raw as Record<string, unknown>)[camel]
  return (value === undefined || value === null ? fallback : value) as T
}

function pickBool(raw: Raw, pascal: string, fallback: boolean): boolean {
  return pick<unknown>(raw, pascal, fallback) === true
}

function pickRaw(raw: Raw, pascal: string): Raw {
  if (!raw || typeof raw !== "object") return null
  const camel = pascal.charAt(0).toLowerCase() + pascal.slice(1)
  return ((raw as Record<string, unknown>)[pascal] ?? (raw as Record<string, unknown>)[camel]) as Raw
}

// ---- Provider configuration ----

function parseOrgField(raw: Raw): OrganizationFieldDefault {
  return {
    title: pick(raw, "Title", ""),
    value: pick(raw, "Value", ""),
    valueDescription: pick<string | null>(raw, "ValueDescription", null),
  }
}

function parseDefaults(raw: Raw): CategoryFieldDefaults {
  return {
    // Provider wire-field names — verbatim, never case-transformed.
    constantFields: (pickRaw(raw, "ConstantFields") as Record<string, unknown>) ?? {},
    organizationFields: pick<Raw[]>(raw, "OrganizationFields", []).map(parseOrgField),
  }
}

function parseMapping(raw: Raw): CompanyMapping {
  return {
    internalOrganizationId: Number(pick(raw, "InternalOrganizationId", 0)) || 0,
    internalOrganizationCode: pick(raw, "InternalOrganizationCode", ""),
    internalOrganizationName: pick(raw, "InternalOrganizationName", ""),
    providerClientId: pick<string | null>(raw, "ProviderClientId", null),
    legalCode: pick<string | null>(raw, "LegalCode", null),
    payGroup: pick<string | null>(raw, "PayGroup", null),
    isExcluded: pickBool(raw, "IsExcluded", false),
  }
}

export function parseProviderConfigData(raw: Raw, provider: string): ProviderConfigurationData {
  const fieldDefaults = pickRaw(raw, "FieldDefaults")
  return {
    provider: pick(raw, "Provider", provider),
    isEnabled: pickBool(raw, "IsEnabled", false),
    providerEnvironmentName: pick(raw, "ProviderEnvironmentName", "sandbox"),
    baseUrl: pick(raw, "BaseUrl", ""),
    providerClientId: pick<string | null>(raw, "ProviderClientId", null),
    companyMappings: pick<Raw[]>(raw, "CompanyMappings", []).map(parseMapping),
    allowedProfileTypes: pick<string[]>(raw, "AllowedProfileTypes", []),
    migrateOnApproval: pickBool(raw, "MigrateOnApproval", true),
    resyncOnChange: pickBool(raw, "ResyncOnChange", false),
    sendTerminations: pickBool(raw, "SendTerminations", false),
    reconcileMissedWorkers: pickBool(raw, "ReconcileMissedWorkers", false),
    migratePerInternalOrganization: pickBool(raw, "MigratePerInternalOrganization", false),
    organizationLastNameSuffix: pick(raw, "OrganizationLastNameSuffix", "Inc"),
    includeHourlyRate: pickBool(raw, "IncludeHourlyRate", false),
    includeNormalHours: pickBool(raw, "IncludeNormalHours", false),
    includeDepartment: pickBool(raw, "IncludeDepartment", false),
    includeWorkersCompCode: pickBool(raw, "IncludeWorkersCompCode", false),
    fieldDefaults: {
      all: parseDefaults(pickRaw(fieldDefaults, "All")),
      individual: parseDefaults(pickRaw(fieldDefaults, "Individual")),
      organization: parseDefaults(pickRaw(fieldDefaults, "Organization")),
    },
  }
}

function serializeDefaults(defaults: CategoryFieldDefaults) {
  return {
    ConstantFields: defaults.constantFields ?? {},
    OrganizationFields: (defaults.organizationFields ?? []).map((f) => ({
      Title: f.title,
      Value: f.value,
      ValueDescription: f.valueDescription ?? null,
    })),
  }
}

export function serializeProviderConfigData(config: ProviderConfigurationData) {
  return {
    Provider: config.provider,
    IsEnabled: config.isEnabled,
    ProviderEnvironmentName: config.providerEnvironmentName,
    BaseUrl: config.baseUrl,
    ProviderClientId: config.providerClientId ?? null,
    CompanyMappings: config.companyMappings.map((m) => ({
      InternalOrganizationId: m.internalOrganizationId,
      InternalOrganizationCode: m.internalOrganizationCode,
      InternalOrganizationName: m.internalOrganizationName,
      ProviderClientId: m.providerClientId ?? null,
      LegalCode: m.legalCode ?? null,
      PayGroup: m.payGroup ?? null,
      IsExcluded: m.isExcluded,
    })),
    AllowedProfileTypes: config.allowedProfileTypes,
    MigrateOnApproval: config.migrateOnApproval,
    ResyncOnChange: config.resyncOnChange,
    SendTerminations: config.sendTerminations,
    ReconcileMissedWorkers: config.reconcileMissedWorkers,
    MigratePerInternalOrganization: config.migratePerInternalOrganization,
    OrganizationLastNameSuffix: config.organizationLastNameSuffix,
    IncludeHourlyRate: config.includeHourlyRate,
    IncludeNormalHours: config.includeNormalHours,
    IncludeDepartment: config.includeDepartment,
    IncludeWorkersCompCode: config.includeWorkersCompCode,
    FieldDefaults: {
      All: serializeDefaults(config.fieldDefaults.all),
      Individual: serializeDefaults(config.fieldDefaults.individual),
      Organization: serializeDefaults(config.fieldDefaults.organization),
    },
  }
}

// ---- Value mapping sets ----

export function parseValueMappingSetData(raw: Raw): ValueMappingSetData {
  return {
    provider: pick(raw, "Provider", ""),
    field: pick(raw, "Field", ""),
    mappings: pick<Raw[]>(raw, "Mappings", []).map(
      (m): ValueMappingRow => ({
        fboValue: pick(m, "FboValue", ""),
        providerValue: pick(m, "ProviderValue", ""),
        description: pick<string | null>(m, "Description", null),
      }),
    ),
  }
}

export function serializeValueMappingSetData(set: ValueMappingSetData) {
  return {
    Provider: set.provider,
    Field: set.field,
    Mappings: set.mappings.map((m) => ({
      FboValue: m.fboValue,
      ProviderValue: m.providerValue,
      Description: m.description ?? null,
    })),
  }
}

// ---- Document envelope (AdminService responses are PascalCase) ----

export interface RawConfigDoc {
  id: string | null
  data: Raw
  /**
   * The document exactly as the server returned it. Updates must spread this
   * and replace only Data — AdminService's update passes the body straight to
   * Cosmos, which rejects a document whose PartitionKey is missing or wrong.
   */
  envelope: Record<string, unknown>
}

export function parseDocEnvelopes(raw: unknown): RawConfigDoc[] {
  if (!Array.isArray(raw)) return []
  return raw.map((d) => ({
    id: (pick<string | null>(d, "Id", null) ?? (d as Record<string, unknown>)?.id ?? null) as string | null,
    data: pickRaw(d, "Data"),
    envelope: (d ?? {}) as Record<string, unknown>,
  }))
}
